import { Autocomplete, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps } from "..";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { produce } from "immer";
import { billingService, cacheService } from "../../../Core/Api/services";
import { ExpenseStatus } from "../../../Core/Api/Types/expense-status";
import { ExpenseTag } from "../../../Core/Api/Types/expense-tag";
import { useTranslation } from "react-i18next";

export type ModalBillingAddExpenseElementProps = {
  invoiceId: number;
  priceModel: any;
  onSuccess: () => void;
  onClose: () => void;
};

export type ModalBillingAddExpenseProps = CommonModalProps<ModalBillingAddExpenseElementProps>;

const ModalBillingAddExpense = React.forwardRef<any, ModalBillingAddExpenseProps>((props, ref) => {
  const { t } = useTranslation();
  const { onClose, onSuccess, closeModal, ...modalProps } = props;
  const { priceModel, invoiceId } = modalProps;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [selected, setSelected] = React.useState<any>(undefined);

  const handleCancelClick = () => {
    closeModal();
    onClose && onClose();
  };

  const onConfirm = useCallback(() => {
    billingService
      .AddExpenseOnInvoice({
        platformId: cacheService.GetUserInfo()?.platformId ?? null,
        invoiceId: invoiceId,
        date: new Date(),
        resourceDate: new Date(),
        resourceId: null,
        titleId: null,
        objectId: null,
        tag: selected.tag ?? ExpenseTag.Corrective,
        price: selected.price,
        note: selected.note,
        status: ExpenseStatus.Bill,
        models: null,
        titleName: null,
        id: 0,
        isNew: true,
        orderCustomer: null,
        orderItemId: null,
        orderNote: null,
        orderPrice: null,
        orderReference: null,
      })
      .then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.expenseAdded"), "success");
          onSuccess();
          handleCancelClick();
        } else enqueueCustomSnackbar(t("alert:error.errorAddingExpense", { message: response.message }), "error");
      });
  }, [invoiceId, selected, t]);

  return (
    <Modal
      title={t("modal:title.modifyInvoice")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={onConfirm} disabled={!selected}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Stack>
          <Typography variant="body1">{t("modal:modalBillingAddExpense.body.selectManagedService")}</Typography>

          <Autocomplete
            value={selected}
            onChange={(e, value) => setSelected(value)}
            options={priceModel.managedServices.services ?? []}
            sx={{ width: "100%", mt: 2 }}
            id={"service"}
            renderInput={(params) => (
              <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("field:placeholder.selectService")} />
            )}
          />
        </Stack>

        <Divider sx={{ mt: 2, mb: 2 }} />

        <Stack>
          <Typography variant="body1">{t("modal:modalBillingAddExpense.body.selectExpenseTag")}</Typography>

          <Autocomplete
            value={selected ? selected.tag : undefined}
            disabled={!selected}
            onChange={(e, value) =>
              setSelected(
                produce((draft) => {
                  if (!draft) return;
                  (draft as any).tag = value;
                })
              )
            }
            options={[ExpenseTag.Corrective, ExpenseTag.ServiceConfig]}
            sx={{ width: "100%", mt: 2 }}
            id={"tag"}
            renderInput={(params) => (
              <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("field:placeholder.selectTag")} />
            )}
          />
        </Stack>

        <Divider sx={{ mt: 2, mb: 2 }} />

        <Stack>
          <Typography variant="body1">{t("modal:modalBillingAddExpense.body.editPrice")}</Typography>

          <TextField
            sx={{ mt: "-10px" }}
            value={selected ? selected.price : 0}
            type="number"
            disabled={!selected}
            onChange={(e) =>
              setSelected(
                produce((draft) => {
                  (draft as any).price = parseFloat(e.target.value.replace(",", "."));
                })
              )
            }
          />
        </Stack>

        <Divider sx={{ mt: 2, mb: 2 }} />

        <Stack>
          <Typography variant="body1">{t("modal:modalBillingAddExpense.body.expenseNote")}</Typography>

          <TextField
            sx={{ mt: "-10px" }}
            value={selected ? selected.note : ""}
            multiline
            placeholder={t("field:placeholder.expenseNote")}
            disabled={!selected}
            onChange={(e) =>
              setSelected(
                produce((draft) => {
                  (draft as any).note = e.target.value;
                })
              )
            }
          />
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalBillingAddExpense;
